<template>
  <b-modal v-model="show" centered hide-header hide-footer modal-class="srt-modal" body-class="srt-modal__body">
    <div class="srt" v-if="show">
      <div class="srt__hero">
        <div class="srt__hero-icon"><i class="fa-solid fa-paper-plane"></i></div>
        <div class="srt__hero-text">
          <div class="srt__hero-kicker">{{ $t('tasks.sendReport') }}</div>
          <h4 class="srt__hero-title">{{ ctx.orgName }}</h4>
        </div>
        <button type="button" class="srt__close" @click="show = false"><i class="fa-solid fa-xmark"></i></button>
      </div>

      <div class="srt__content">
        <div class="srt__field">
          <label class="srt__label">{{ $t('tasks.reportSegment') }}</label>
          <v-select v-model="segment" :options="segmentOptions" :reduce="o => o.value" label="label" :clearable="false" @input="loadCount"/>
        </div>

        <div class="srt__count">
          <b-spinner small v-if="countLoading"/>
          <template v-else><strong>{{ count }}</strong> {{ $t('tasks.clientsToReview') }}</template>
        </div>

        <div class="srt__field">
          <label class="srt__label">{{ $t('columns.assignee') }}</label>
          <user-select-box v-model="assigneeUserId" :clearable="true"/>
          <small class="text-muted" v-if="suggestedHint">{{ $t('tasks.suggestedManager') }}: {{ suggestedHint }}</small>
        </div>

        <div class="srt__grid">
          <div class="srt__field">
            <label class="srt__label">{{ $t('columns.priority') }}</label>
            <v-select v-model="priority" :options="priorityOptions" :reduce="o => o.value" label="label" :clearable="false"/>
          </div>
          <div class="srt__field">
            <label class="srt__label">{{ $t('columns.dueDate') }}</label>
            <b-form-datepicker v-model="dueDate" :locale="$i18n.locale" reset-button
                               :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"/>
          </div>
        </div>

        <div class="srt__field">
          <label class="srt__label">{{ $t('tasks.note') }}</label>
          <b-form-textarea v-model="note" rows="2" :placeholder="$t('tasks.notePlaceholder')"/>
        </div>
      </div>

      <div class="srt__footer">
        <b-button variant="outline-secondary" @click="show = false">{{ $t('operations.cancel') }}</b-button>
        <b-button variant="primary" :disabled="!assigneeUserId || sending" @click="send">
          <b-spinner small v-if="sending" class="mr-50"/><i class="fa-solid fa-paper-plane mr-50"></i>{{ $t('tasks.sendTask') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import mixinNotifications from '@/components/mixin/mixinNotifications'
import UserSelectBox from '@/components/widget/select/UserSelectBox'
import { TASK_PRIORITIES } from '@/model/task/taskTypeMeta'

export default {
  name: 'SendReportTaskModal',
  components: { UserSelectBox },
  mixins: [mixinNotifications],
  data: () => ({
    show: false,
    sending: false,
    ctx: {},
    segment: 'age30plus',
    assigneeUserId: null,
    suggestedHint: '',
    priority: 'HIGH',
    dueDate: null,
    note: '',
    count: 0,
    countLoading: false,
  }),
  computed: {
    segmentOptions() {
      const segments = (this.ctx.segments && this.ctx.segments.length) ? this.ctx.segments : ['age0to15', 'age15to30', 'age30plus', 'total']
      return segments.map(s => ({ value: s, label: this.$t('clientDashboard.' + s) }))
    },
    priorityOptions() { return TASK_PRIORITIES.map(p => ({ value: p, label: this.$t('taskPriorities.' + p) })) },
  },
  methods: {
    ...mapActions('tasks', ['createTask', 'getSuggestedAssignees']),
    ...mapActions('dashboard', ['getClientDashboardClients']),
    open(params) {
      this.ctx = params || {}
      this.segment = params.segment || 'age30plus'
      this.assigneeUserId = null
      this.suggestedHint = ''
      this.priority = 'HIGH'
      this.dueDate = this.$moment().add(1, 'day').format('YYYY-MM-DD')
      this.note = ''
      this.count = params.count || 0
      this.show = true
      this.loadSuggested()
      this.loadCount()
    },
    loadSuggested() {
      if (!this.ctx.orgId) return
      this.getSuggestedAssignees(this.ctx.orgId).then(list => {
        if (Array.isArray(list) && list.length) {
          this.assigneeUserId = list[0].id
          this.suggestedHint = list.map(u => u.fullName).filter(Boolean).join(', ')
        }
      }).catch(() => {})
    },
    loadCount() {
      this.countLoading = true
      this.getClientDashboardClients({
        segment: this.segment,
        orgIds: [this.ctx.orgId],
        buIds: [], buTypes: [],
        range: this.ctx.range || { from: null, until: null },
        pagination: { page: 1, size: 1 },
      }).then(
        res => { this.count = (res.metadata && res.metadata.total != null) ? res.metadata.total : 0 },
        () => {}
      ).finally(() => { this.countLoading = false })
    },
    send() {
      this.sending = true
      const segLabel = this.$t('clientDashboard.' + this.segment)
      const task = {
        type: 'REVIEW_CLIENT_REPORT',
        title: `${this.ctx.orgName} · ${segLabel} (${this.count})`,
        description: this.note || null,
        assigneeUserId: this.assigneeUserId,
        priority: this.priority,
        dueDate: this.dueDate ? this.dueDate + ' 23:59' : null,
        relatedModule: 'REPORT',
        relatedId: this.ctx.orgId,
        context: {
          segment: this.segment,
          orgIds: [this.ctx.orgId],
          orgName: this.ctx.orgName,
          range: this.ctx.range || { from: null, until: null },
          count: this.count,
        },
      }
      this.createTask({ task }).then(
        () => {
          this.showSuccessNotification(this.$t('tasks.reportSent'))
          this.show = false
          this.$emit('sent')
        },
        err => this.showServerErrorNotification('createTask', 'tasks', err)
      ).finally(() => { this.sending = false })
    },
  },
}
</script>

<style lang="scss">
.srt-modal {
  // NOTE: no overflow:hidden here — it would clip the date-picker dropdown.
  // The hero rounds its own top corners instead.
  .modal-content { border: none; border-radius: 18px; box-shadow: 0 24px 60px -20px rgba(20,24,50,0.45); }
  .modal-body.srt-modal__body { padding: 0; }
}
.srt {
  &__hero { display: flex; align-items: center; gap: 1rem; padding: 1.1rem 1.4rem; color: #fff; background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%); border-radius: 18px 18px 0 0; }
  &__hero-icon { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 12px; font-size: 1.15rem; background: rgba(255,255,255,0.22); flex-shrink: 0; }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-kicker { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.9; font-weight: 700; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.15rem; }
  &__close { border: none; background: rgba(255,255,255,0.18); color: #fff; width: 32px; height: 32px; border-radius: 9px; cursor: pointer; transition: all .2s ease; &:hover { background: rgba(255,255,255,0.32); transform: rotate(90deg); } }
  &__content { padding: 1.2rem 1.4rem 0.4rem; display: flex; flex-direction: column; gap: 0.8rem; }
  &__grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.8rem; }
  &__label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: #6e6b7b; margin-bottom: 0.3rem; display: block; }
  &__count { background: #eef4ff; color: #007BFF; border-radius: 10px; padding: 0.5rem 0.9rem; font-size: 0.95rem; font-weight: 600; }
  &__footer { display: flex; justify-content: flex-end; gap: 0.6rem; padding: 0.8rem 1.4rem 1.3rem; }
  @media (max-width: 575px) { &__grid { grid-template-columns: 1fr; } }
}
// keep the date label on one line inside the narrow field
.srt-modal .b-form-datepicker > label.form-control {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; height: auto;
}
body.dark-layout {
  .srt-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .srt__count { background: rgba(0,123,255,0.16); color: #6cb2ff; }
}
</style>
